/**
 * The local IndexedDB database, via Dexie 4.
 *
 * ONE version, deliberately. The app had a v1–v6 ladder while it was being
 * built; it has never been deployed, so every install is a fresh one and the
 * ladder was collapsed into this single declaration rather than carried
 * forever. Dexie allows that — since 3.0 a historical version declaration only
 * has to be kept if it carries an `.upgrade()` function, and none of ours did.
 *
 * The consequence, which is the price of the collapse: a device that still
 * holds the old v6 database CANNOT open this one. `db.open()` calls
 * `indexedDB.open(name, version)` and IndexedDB refuses to open below the
 * on-disk version, so the promise rejects with a VersionError. That is handled
 * explicitly at boot (lib/db-open-guard.ts) rather than left to fail silently.
 * From here on, a schema change is a normal `db.version(2)` bump.
 *
 * The database name carries the `-v1` suffix from docs/plan.md. That suffix is
 * a *schema-family* marker for a deliberate throw-it-away migration, not the
 * Dexie version number.
 */
import { Dexie, type EntityTable } from 'dexie';
import type {
  BuyerDirectoryEntry,
  Category,
  CreateLeadInput,
  Finish,
  LeadWithItems,
  Material,
  ProductWithPhotos,
  StageChangeInput,
  UpdateLeadInput
} from '@paradise/shared';
import type { DraftItem } from '@/lib/capture-draft';
import type { LeadFormValues } from '@/components/capture/lead-form-values';

/**
 * The mirrored session. Written on every successful `/get-session`, read at
 * boot before the network is consulted at all.
 *
 * `expiresAt` is epoch milliseconds, taken from Better Auth's own
 * `session.expiresAt` — NOT a locally-invented TTL. It is the horizon the
 * offline boot path measures against: rcrcrc's five-minute cache TTL would
 * mean the app stops trusting itself after five minutes in a hall with no
 * signal, which is a normal Tuesday at a booth.
 *
 * Only the fields the app actually renders or authorises against are mirrored.
 * This row sits in IndexedDB unencrypted on a sales phone, so it is
 * deliberately not the whole Better Auth user object.
 */
export interface CachedSession {
  /** Single-row table; always the literal 'current'. */
  key: 'current';
  userId: string;
  email: string;
  name: string;
  /** Better Auth's admin plugin role, when the server sends one. */
  role: string | null;
  /** The tradeshow the device is currently working, when one is selected. */
  currentEventId: string | null;
  /** Epoch ms. Better Auth's `session.expiresAt`. */
  expiresAt: number;
  /** Epoch ms this mirror was last confirmed by the server. Diagnostics only. */
  cachedAt: number;
}

/** Untyped key/value scratch space (last sync time, UI preferences, …). */
export interface MetaEntry {
  key: string;
  value: unknown;
}

// ---------------------------------------------------------------------------
// v2 — the offline core
// ---------------------------------------------------------------------------

/**
 * The three mutation kinds the outbox can carry.
 *
 * Only `lead.create` is ever enqueued OFFLINE — capture happens at a booth with
 * unreliable signal. `lead.update` and `lead.stageChange` require connectivity
 * (their controls are disabled offline via `useIsOnline()`); they are modelled
 * here so the drain can replay one that was enqueued on a flaky-but-present
 * connection, but the capture form is the only offline producer. See
 * docs/plan.md, Offline Resilience.
 */
export type OutboxKind = 'lead.create' | 'lead.update' | 'lead.stageChange';

export type OutboxStatus = 'pending' | 'in_flight' | 'done' | 'failed_transient' | 'failed_permanent';

/**
 * `lead.create` — the whole booth conversation in one entry. The card photo
 * bytes do NOT live in the payload: they sit in `cardBlobs` keyed by the same
 * `clientUuid`, and the drain streams them into the multipart request. This is
 * why there is no `photo.upload` kind — one queue entry, one request, one
 * idempotency key covering the lead and its card (docs/plan.md, "The card photo
 * travels inside the lead mutation").
 */
export interface LeadCreatePayload {
  input: CreateLeadInput;
}

/** `lead.update` — replaces editable fields on an existing lead (online-only producer). */
export interface LeadUpdatePayload {
  leadId: string;
  input: UpdateLeadInput;
}

/** `lead.stageChange` — the one-field stage move (online-only producer). */
export interface LeadStageChangePayload {
  leadId: string;
  input: StageChangeInput;
}

export type OutboxPayload = LeadCreatePayload | LeadUpdatePayload | LeadStageChangePayload;

/**
 * One queued mutation. `clientUuid` is the primary key AND the server-side
 * idempotency token, so replaying a request that actually succeeded is a safe
 * no-op rather than a duplicate.
 *
 * Display fields (`company`) are denormalised onto the row so the Sync-issues
 * screen can list a stuck entry without decoding its payload — and so a discard
 * confirmation can name the company being destroyed.
 */
export interface OutboxRow {
  /** Client-generated uuid v4, made BEFORE submit. PK + idempotency key. */
  clientUuid: string;
  kind: OutboxKind;
  payload: OutboxPayload;
  status: OutboxStatus;
  /** Whether a FRONT card blob is queued for this entry (drives the multipart part). */
  hasCard: boolean;
  /** Whether a BACK card blob is queued for this entry. */
  hasCardBack: boolean;
  /** Denormalised for the Sync-issues list; never authoritative. */
  company: string;
  /** `Date.now()` at enqueue — the FIFO drain order. */
  createdAt: number;
  /** Bumped by every drain attempt; drives the backoff ladder. */
  attempts: number;
  /**
   * Attempts that actually REACHED the server. Only these spend the
   * give-up-and-park budget: a device in a dead zone racks up `attempts` on
   * backoff alone, and counting those parked captured leads as
   * `failed_permanent` for the sin of being written with no signal. Absent on
   * rows written before this field existed — read it as `?? 0`.
   */
  serverAttempts?: number;
  /**
   * `Date.now()` of the last in-place edit of this entry, for a `lead.create`
   * amended before it reached the server (`amendLeadCreate`). Its only job is
   * to gate the drain's reconcile check: the server's replay branch returns an
   * EXISTING lead for a known `clientUuid` and never applies the new payload,
   * so an entry that was edited has to have what came back compared against
   * what it now holds. Never set on an entry that was never edited, which is
   * what keeps the comparison off the ordinary path.
   */
  amendedAt?: number | null;
  /** `Date.now()` of the most recent attempt; gates exponential backoff. */
  lastAttemptAt: number | null;
  /** The canonical `ErrorCode` (or an offline reason) from the last attempt. */
  lastErrorCode: string | null;
  /** Human copy from the last failure — shown, never branched on. */
  lastError: string | null;
}

/**
 * The optimistic local copy of a lead created on THIS device. Rendered on the
 * board with a "Pending" ref until the drain assigns the server `ref`. Distinct
 * from the `leads` mirror, which is a read-only copy of the SERVER's leads.
 */
export interface LeadDraftRow {
  /** Same uuid as the outbox row + the eventual `Lead.clientUuid`. */
  clientUuid: string;
  status: 'pending_sync' | 'synced' | 'failed';
  /** Server-allocated ref once the drain succeeds; null → UI shows "Pending". */
  ref: number | null;
  /** Server `Lead.id` once synced. */
  serverId: string | null;
  /** Snapshot so the board renders without touching the outbox row. */
  input: CreateLeadInput;
  createdAt: number;
  updatedAt: number;
}

/**
 * The read-only mirror of the server's active leads (with items), so the board
 * and detail views work offline. FULL-REPLACED on every successful
 * `GET /api/leads` — never incrementally patched (docs/plan.md, Reading
 * existing leads offline). Card scans are deliberately NOT mirrored.
 */
export type LeadMirrorRow = LeadWithItems;

/**
 * Captured business-card JPEG bytes awaiting sync. Stored as **ArrayBuffer, not
 * Blob** — some Android Chrome builds garbage-collect opaque Blob handles
 * between sessions (coding-standards.md, Offline-affecting code). Reconstructed
 * into a Blob only at drain time via `new Blob([bytes], { type })`.
 */
export interface CardBlobRow {
  /** Same uuid as the lead it belongs to. */
  clientUuid: string;
  bytes: ArrayBuffer;
  contentType: string;
  sizeBytes: number;
  createdAt: number;
}

/** A catalogue product with its photo gallery — the offline product list. */
export type CatalogueRow = ProductWithPhotos;

/** A precached buyer-directory entry, keyed by company for dedup + autofill. */
export interface BuyerDirectoryRow extends BuyerDirectoryEntry {
  /** Lowercased company, the primary key — the dedup boundary the server uses. */
  companyKey: string;
}

// ---------------------------------------------------------------------------
// Master-data mirrors: categories, materials and finishes, mirrored the same
// way `catalogue` mirrors products (docs/plan.md; coding-standards.md,
// Offline-affecting code).
// ---------------------------------------------------------------------------

/** A catalogue category — the offline mirror behind the category chips. */
export type CategoryRow = Category;

/** A catalogue material — the offline mirror behind product material names. */
export type MaterialRow = Material;

/** A catalogue finish — the offline mirror behind product finish names. */
export type FinishRow = Finish;

// ---------------------------------------------------------------------------
// Event↔product tags, mirrored so the catalogue's "filter by event" works
// offline (a per-event fetch would not survive a dead zone).
// ---------------------------------------------------------------------------

/**
 * One event↔product tag pair. `id` is the synthetic `${eventId}:${productId}`
 * (the composite key flattened to a string PK); `eventId` is indexed so the
 * browse-by-event filter reads one event's tagged product ids with a single
 * indexed query. Full-replaced on every pull, like the other read mirrors.
 */
export interface EventTagRow {
  id: string;
  eventId: string;
  productId: string;
}

// ---------------------------------------------------------------------------
// In-progress capture autosave, so an interrupted lead capture (the OS
// reclaiming the renderer while the camera is foregrounded, or an accidental
// reload) can be RECOVERED rather than silently lost. This is the durable
// backstop behind the in-memory capture form + `useCaptureDraft` scratchpad.
// ---------------------------------------------------------------------------

/**
 * A debounced snapshot of the new-lead capture form. `key` is a fixed literal
 * (`'lead:new'`) — one in-progress new-lead capture at a time. Card bytes are
 * an **ArrayBuffer, not a Blob** (coding-standards.md, Offline-affecting code).
 * Cleared on a successful save and on an explicit discard; it only lingers
 * after an interrupted session, which is exactly when it is wanted.
 */
export interface CaptureAutosaveRow {
  key: string;
  values: LeadFormValues;
  items: DraftItem[];
  cardFront: { bytes: ArrayBuffer; contentType: string } | null;
  cardBack: { bytes: ArrayBuffer; contentType: string } | null;
  cardFrontRemoved: boolean;
  cardBackRemoved: boolean;
  updatedAt: number;
}

export const CAPTURE_AUTOSAVE_NEW_KEY = 'lead:new' as const;

export const db = new Dexie('paradise-leads-v1') as Dexie & {
  session: EntityTable<CachedSession, 'key'>;
  meta: EntityTable<MetaEntry, 'key'>;
  outbox: EntityTable<OutboxRow, 'clientUuid'>;
  leadsDraft: EntityTable<LeadDraftRow, 'clientUuid'>;
  leads: EntityTable<LeadMirrorRow, 'id'>;
  cardBlobs: EntityTable<CardBlobRow, 'clientUuid'>;
  cardBackBlobs: EntityTable<CardBlobRow, 'clientUuid'>;
  catalogue: EntityTable<CatalogueRow, 'id'>;
  buyers: EntityTable<BuyerDirectoryRow, 'companyKey'>;
  categories: EntityTable<CategoryRow, 'id'>;
  materials: EntityTable<MaterialRow, 'id'>;
  finishes: EntityTable<FinishRow, 'id'>;
  eventTags: EntityTable<EventTagRow, 'id'>;
  captureAutosave: EntityTable<CaptureAutosaveRow, 'key'>;
};

db.version(1).stores({
  session: 'key',
  meta: 'key',
  outbox: '&clientUuid, status, createdAt, kind',
  leadsDraft: '&clientUuid, status, createdAt',
  leads: '&id, eventId, updatedAt',
  cardBlobs: '&clientUuid',
  cardBackBlobs: '&clientUuid',
  catalogue: '&id, itemNo',
  buyers: '&companyKey, lastSeenAt',
  categories: '&id, sortOrder',
  materials: '&id, sortOrder',
  finishes: '&id, sortOrder',
  eventTags: '&id, eventId, productId',
  captureAutosave: '&key'
});

export const SESSION_ROW_KEY = 'current' as const;

// ---------------------------------------------------------------------------
// Meta helpers — typed keys so callers don't sprinkle raw strings.
// ---------------------------------------------------------------------------

export type MetaKey = 'lastSyncAt' | 'catalogueVersion' | 'buyersSyncAt' | 'deviceId';

export async function metaGet<T = unknown>(key: MetaKey): Promise<T | null> {
  const row = await db.meta.get(key);
  return row ? (row.value as T) : null;
}

export async function metaSet(key: MetaKey, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

// ---------------------------------------------------------------------------
// The unsynced-work seam
// ---------------------------------------------------------------------------

/**
 * Set by the offline-core task once the outbox exists. Until then it is null,
 * and `countUnsyncedWork()` reports "unknowable" rather than a fabricated 0.
 *
 * This is deliberately a registration hook rather than a direct import of an
 * outbox module: this file must not grow a dependency on code that does not
 * exist yet, and a stubbed `return 0` here would silently become a lie the day
 * the outbox lands — the logout guard would read "nothing pending" forever.
 */
let unsyncedWorkCounter: (() => Promise<number>) | null = null;

export function registerUnsyncedWorkCounter(counter: () => Promise<number>): void {
  unsyncedWorkCounter = counter;
}

/**
 * How many captured-but-unsynced mutations are sitting in local storage.
 *
 * Returns `null` when the outbox has not been built yet — the caller MUST
 * treat that as "cannot know", never as zero. See `signOut()` in
 * lib/auth/session.ts for the guard this feeds.
 */
export async function countUnsyncedWork(): Promise<number | null> {
  if (!unsyncedWorkCounter) return null;
  return unsyncedWorkCounter();
}

// ---------------------------------------------------------------------------
// Session mirror accessors
// ---------------------------------------------------------------------------

export async function readCachedSession(): Promise<CachedSession | undefined> {
  return db.session.get(SESSION_ROW_KEY);
}

export async function writeCachedSession(entry: Omit<CachedSession, 'key' | 'cachedAt'>): Promise<void> {
  await db.session.put({ ...entry, key: SESSION_ROW_KEY, cachedAt: Date.now() });
}

/**
 * Drops the session mirror and nothing else.
 *
 * This is the response to a *revoked or expired* session: the device can no
 * longer talk to the API, but the leads it captured at a booth may exist
 * nowhere else in the world. Destroying them because a session lapsed would
 * turn an inconvenience into data loss. Wiping the rest of the database is
 * `clearAllLocalData()`, reached only from an explicit, confirmed logout.
 */
export async function clearCachedSession(): Promise<void> {
  await db.session.delete(SESSION_ROW_KEY);
}

/**
 * Wipes the entire local database — the session mirror, and (once they exist)
 * the catalogue, the cached leads and every buyer's name, phone and email.
 *
 * Killing a session server-side stops a lost phone reaching the API; it does
 * nothing whatsoever to the copy of the buyer directory already sitting in its
 * IndexedDB. Clearing on logout is the only lever this app actually has over
 * that data, which is why logout wipes everything rather than just signing out.
 */
export async function clearAllLocalData(): Promise<void> {
  await db.delete();
  await db.open();
}
